import { createContext, ReactNode } from 'react';
import { Assemblage } from '../../../types/Assemblage';
import { ClassDetail } from 'edrolo/types/ClassDetail';
import { getIsTeacherView } from '../utils/getIsTeacherView';
import {
  getStaffPositionOverride,
  getStaffFeatureSetOverride,
  useForceRerender,
} from 'edrolo/domains/utils/staffOverrideUtils';
import {
  useClassAssemblage,
  useClassDetail,
} from 'edrolo/domains/bySchoolClass';
import { useProfile, YEAR_LEVEL, CLASS_POSITION } from 'edrolo/domains/profile';

interface Props {
  classId: ClassId;
  courseId?: number;
  children: ReactNode;
}

interface iClassContext {
  isTeacherView: boolean;
  studentNotesEnabled: boolean;
  showMetadata: boolean;
  classId: ClassId;
  courseId?: number;
  assemblage: Assemblage;
  classDetail: ClassDetail;
  classFeatureSet?: string;
  forceRerender: () => void;
}

interface ReturnValue {
  data: iClassContext;
  loading: boolean;
  error: Error | null;
}

const ClassContext = createContext({} as ReturnValue);

const ClassProvider = ({ classId, courseId, children }: Props) => {
  const { profile } = useProfile();

  const {
    data: assemblage,
    loading: assemblageLoading,
    error: assemblageError,
  } = useClassAssemblage(classId, courseId);

  const {
    classDetail,
    loading: classDetailLoading,
    error: classDetailError,
  } = useClassDetail(classId);

  const loading = assemblageLoading || classDetailLoading;
  const error = assemblageError || classDetailError;

  const overriddenClassPosition =
    getStaffPositionOverride() || CLASS_POSITION.STUDENT; // fallback to student

  const isTeacherView = getIsTeacherView(
    profile,
    classId,
    overriddenClassPosition
  );

  const isJunior =
    profile.estimated_year_level === YEAR_LEVEL.YEAR_7 ||
    profile.estimated_year_level === YEAR_LEVEL.YEAR_8 ||
    profile.estimated_year_level === YEAR_LEVEL.YEAR_9 ||
    profile.estimated_year_level === YEAR_LEVEL.YEAR_10;

  const data: iClassContext = {
    classId,
    courseId,
    assemblage,
    isTeacherView: isTeacherView,
    classFeatureSet: getStaffFeatureSetOverride(),
    studentNotesEnabled: !!assemblage?.course.show_student_notes,
    showMetadata: isTeacherView || !isJunior,
    classDetail: classDetail.toJS(),
    forceRerender: useForceRerender(),
  };

  if (error) {
    console.error('There was an error loading data to ClassContext', error);
  }

  return (
    <ClassContext.Provider value={{ data, loading, error }}>
      {children}
    </ClassContext.Provider>
  );
};

export { ClassContext, ClassProvider };
